// Strip field: fitter dropdown or selected pill with Change link.
// "Any Fitter" is the default. The list comes from /resources filtered to the
// selected location.

import { useEffect, useState } from 'react';
import styles from '../styles/widget.module.css';
import { listResources } from '../api/client';
import type { Resource } from '../types';
import type { BookingAction, BookingState } from '../state/machine';
import { useClickOutside } from '../hooks/useClickOutside';
import { useOpenClose } from '../hooks/useOpenClose';
import ChevronDown from '../assets/icons/ChevronDown';
import Info from '../assets/icons/Info';
import SimplePopup from './SimplePopup';

interface FitterFieldProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  disabled?: boolean;
}

export function FitterField({ state, dispatch, disabled = false }: FitterFieldProps) {
  const [fitters, setFitters] = useState<Resource[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const { isOpen: isOpenFitterInfo, close: closeFitterInfo, open: openFitterInfo } = useOpenClose();

  const wrapperRef = useClickOutside<HTMLDivElement>(() => {
    setOpen(false);
  }, { enabled: open });

  useEffect(() => {
    if (!state.selectedLocation) return;
    listResources()
      .then((all) =>
        setFitters(all.filter((r) => r.locationId === state.selectedLocation!.id)),
      )
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load fitters'));
  }, [state.selectedLocation]);

  const selectedFitter = state.selectedFitterId
    ? fitters?.find((f) => f.id === state.selectedFitterId)
    : null;

  if (selectedFitter) {
    return (
      <div className={styles.stripPill}>
        <span className={styles.label}>Fitter</span>
        <div className={styles.stripPillValue}>
          <span>{selectedFitter.name}</span>
          <button
            className={styles.stripPillChange}
            onClick={() => dispatch({ type: 'SELECT_FITTER', payload: null })}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.field} ${disabled ? styles.panelSectionDisabled : ''}`}
      style={{ position: 'relative' }}
      ref={wrapperRef}
    >
      <label className={styles.label}>
        Fitter{' '}
        <button type="button" onClick={openFitterInfo} className={styles.infoPopupTrigger}>
          <Info />
        </button>
      </label>

      {disabled && (
        <span className={styles.disabledFieldMessage}>Select a fitting type to enable this field</span>
      )}

      <SimplePopup
        children={
          <div>
            <h3>WHAT MAKES A MASTER FITTER</h3>
            <p>
              Our master fitters graduate from Club Champion University with over 250 hours of classroom
              and in bay fitting experience.To stay ahead of the curve, they undergo rigorous, ongoing
              education directly from golf&apos;s top manufacturers every single year. This continuous
              training builds unmatched, cross-brand product knowledge, ensuring they know exactly how
              every head and shaft combination performs for your unique swing. If you have an existing
              fitter relationship, choose that fitter here. If not, a master fitter will be assigned to
              you.
            </p>
          </div>
        }
        isOpen={isOpenFitterInfo}
        onClosePopup={closeFitterInfo}
        className={styles.fitterInfoPopup}
      />

      {error && <div className={styles.error}>{error}</div>}

      <button
        className={styles.input}
        style={{ textAlign: 'left', cursor: 'pointer', background: '#fff' }}
        onClick={() => {
          if (disabled) return;
          setOpen(!open);
        }}
      >
        <span>Any Fitter</span>
        <ChevronDown />
      </button>

      {open && (
        <div className={styles.locationDropdown}>
          {!fitters ? (
            <div className={styles.locationDropdownHeader}>
              <span className={styles.spinner} /> Loading fitters…
            </div>
          ) : (
            <>
              <div className={styles.locationDropdownHeader}>
                {fitters.length + 1} Fitters to Choose From
              </div>
              <div
                className={styles.locationRow}
                onClick={() => {
                  if (disabled) return;
                  dispatch({ type: 'SELECT_FITTER', payload: null });
                  setOpen(false);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (disabled) return;
                    dispatch({ type: 'SELECT_FITTER', payload: null });
                    setOpen(false);
                  }
                }}
              >
                <div className={styles.locationName}>ANY FITTER</div>
              </div>
              {fitters.map((f) => (
                <div
                  key={f.id}
                  className={styles.locationRow}
                  onClick={() => {
                    if (disabled) return;
                    dispatch({ type: 'SELECT_FITTER', payload: f.id });
                    setOpen(false);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (disabled) return;
                      dispatch({ type: 'SELECT_FITTER', payload: f.id });
                      setOpen(false);
                    }
                  }}
                >
                  <div className={styles.locationName}>{f.name.toUpperCase()}</div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
